import React from "react"

import Layout from "../../components/layout"
import SEO from "../../components/seo"
import TextBlock from "../../components/textBlock"
import Section from "../../components/section"
import Carousel from "../../components/carousel"
import Item from "../../components/item"
import Numbers from "../../components/numbers"

import HeroSVG from "../../images/solutions/agent_efficiency/agent_effenciency.svg"

import actionSVG from "../../images/solutions/agent_efficiency/action.svg"
import AiAutomationSVG from "../../images/solutions/agent_efficiency/ai_automation.svg"
import AnalyticsSVG from "../../images/solutions/agent_efficiency/analytics.svg"
import ChatSVG from "../../images/solutions/agent_efficiency/chat.svg"
import CollaborationSVG from "../../images/solutions/agent_efficiency/collaboration.svg"
import EmailSVG from "../../images/solutions/agent_efficiency/email.svg"
import InstantBcdr from "../../images/solutions/agent_efficiency/instant-bcdr.svg"
import IntegrationSVG from "../../images/solutions/agent_efficiency/integration.svg"
import SocialSVG from "../../images/solutions/agent_efficiency/social.svg"
import TalkAnswersSVG from "../../images/solutions/agent_efficiency/talk_answers.svg"
import VoiceSVG from "../../images/solutions/agent_efficiency/voice.svg"
import VoiceAnalyticsSVG from "../../images/solutions/agent_efficiency/voice-analytics.svg"

import AgentEngagementSVG from "../../images/solutions/agent_efficiency/agent-engagement.svg"
import SavingSVG from "../../images/solutions/agent_efficiency/saving.svg"
import PreparedAgentSVG from "../../images/solutions/agent_efficiency/prepared_agent.svg"
import QualityManagementSVG from "../../images/solutions/agent_efficiency/quality_management.svg"

const carouselData = [
  {
    title: "Agent Efficiency.",
    subtitle:
      "Increase customer satisfaction and reduce your operating costs with unified channels, blended automation, and contact with context.",
    image: HeroSVG,
  },
]

const numberData = [
  {
    value: "40%",
    label: "Chat Deflection.",
  },
  {
    value: "76%",
    label: "Bot Involvement.",
  },
]

const itemsData = [
  { name: "Voice", image: VoiceSVG },
  { name: "Chat", image: ChatSVG },
  { name: "Email", image: EmailSVG },
  { name: "Social", image: SocialSVG },
  { name: "Internal collaboration", image: CollaborationSVG },
  { name: "Real time insights", image: AnalyticsSVG },
  { name: "Easy integrations", image: IntegrationSVG },
  { name: "Instant BC/DR", image: InstantBcdr },
  { name: "Voice analytics", image: VoiceAnalyticsSVG },
  { name: "Next best action", image: actionSVG },
  { name: "QA", image: TalkAnswersSVG },
  { name: "Automation", image: AiAutomationSVG },
]

const AgentEfficiency = () => {
  return (
    <Layout>
      <SEO
        title="Powering Contact Centre Agent Efficiency To Reduce Operating Costs"
        description="Increase customer satisfaction and reduce your contact centre operating costs with unified channels, blended automation and contact with context. "
        keywords="digital customer experience, ai, artificial intelligence, natural language processing, amazon lex, amazon polly, omnichannel cloud, uk technology companies, cloud communications, cloud phone provider, manchester tech, tech startup, mobile technology, app development, software companies, tech startup manchester, tech company lancashire, manchester digital, circleloop, smartagent, aws partner"
        robots="index, follow"
        revisit_after="1 month"
      />
      <Carousel data={carouselData} />
      <TextBlock
        contrast
        title="<span>Your contact centre technology is probably </span> holding back efficiency."
      >
        <p>
          Traditional contact centre solutions come with constraints.
          Challenging integrations, difficult system administration and slow
          moving vendors often mean progress is stifled, and the efficiency
          gains you’d hoped for take too long to materialise.
        </p>
        <p>
          Our technology provides the flexibility you need to remove high volume
          contact and reduce agent handling times, at your pace and on your
          terms.
        </p>
      </TextBlock>
      <section className="centre section-padding">
        <h2>
          A single pane of glass for{" "}
          <span className="highlight">every channel.</span>
        </h2>
        <hr />
        <p className="hightlight">
          Our SmartAgent application provides your agents with everything they
          need to manage customer contact in an intuitive browser-based agent
          desktop.
        </p>
        <div className="centre items items--center items--four-cols">
          {itemsData.map((data, idx) => (
            <Item name={data.name} image={data.image} key={idx} />
          ))}
        </div>
      </section>

      <Section image={AgentEngagementSVG} reverse noparagraph padded>
        <h2>
          Responding to an{" "}
          <span className="highlight">ever-changing landscape.</span>
        </h2>
        <hr />
        <p>
          With everything in one easy to use interface, and smart AI tools that
          surface knowledge and information in real time, agent training and
          onboarding is a breeze. Whilst real time insights help you to spot
          trends and issues enabling intra-day response to what matters the
          most.
        </p>
      </Section>

      <Section image={SavingSVG} noparagraph padded>
        <h2>
          Driving up quality,{" "}
          <span className="highlight">driving down costs.</span>
        </h2>
        <hr />
        <p>
          On board Quality Management enables QAs and Supervisors to review the
          full detail of customer interactions, and quickly score them against
          your calibrated processes. This feedback is then available to the
          agent in real time, helping you to provide coaching and training on
          the fly. Dynamic scripting and next-best action also help drive up the
          quality of conversations and reduce handling times.
        </p>
      </Section>

      <Section image={PreparedAgentSVG} reverse noparagraph padded>
        <h2>
          Freeing your agents to{" "}
          <span className="highlight">focus on value</span>
        </h2>
        <hr />
        <p>
          There is a sweet spot between contact automation and the human touch.
          Enabling your agents to do their job to the best of their ability is a
          no brainer, but that’s only half of the story. Implementing
          intelligent contact automation to deflect or shift high volume contact
          gives your agents time to focus on more meaningful interactions.
        </p>
        <p>
          We will help you find the right blend for your business, so that your
          customers get the answers they need, when they need them. Whilst
          freeing up your human resource to really focus in on the contact that
          matters.
        </p>
      </Section>
      <Numbers contrast data={numberData} />

      <Section image={QualityManagementSVG} noparagraph padded>
        <h2>
          Connect with virtually
          <span className="highlight"> anything, and anyone</span>
        </h2>
        <hr />
        <p>
          Modern, open architecture enables easy integration into the systems
          your agents rely on. We’ve already developed integrations with
          hundreds of systems from CRM and Service Desks through to Workforce
          Management and Payment Service Providers. By streamlining all of your
          information sources and contact channels into a single interface,
          agents have everything they need at their fingertips.
        </p>
      </Section>
    </Layout>
  )
}

export default AgentEfficiency
